import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { LockKey, CreditCard, ArrowLeft, EnvelopeSimple, Eye, MagnifyingGlass, DownloadSimple, BellRinging, ArrowSquareOut, Timer } from "@phosphor-icons/react";
import { toast } from "sonner";

const money = (n, cur = "gbp") => (n == null ? "" : new Intl.NumberFormat("en-GB", { style: "currency", currency: (cur || "gbp").toUpperCase() }).format(Number(n)));
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "");

const STILL_WORKS = [
  { icon: Eye, label: "Viewing jobs, quotes, invoices and documents" },
  { icon: MagnifyingGlass, label: "Searching and filtering everything" },
  { icon: DownloadSimple, label: "Downloading PDFs and exports" },
  { icon: BellRinging, label: "Notifications and your profile" },
];

export default function WorkspaceLockedPage() {
  const { user, checkAuth } = useAuth();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const from = state?.from && state.from !== "/workspace-locked" ? state.from : "/";

  useEffect(() => {
    let alive = true;
    const load = () => API.get("/billing/lock-status").then(({ data }) => {
      if (!alive) return;
      setInfo(data);
      if (!data.dunning?.locked) {
        toast.success(data.reason === "trial_expired" || data.status === "active" ? "Subscription active — full access is back" : "Payment received — full access is back");
        checkAuth?.();
        navigate(from, { replace: true });
      }
    }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [from, navigate, checkAuth]);

  const updateCard = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open the Stripe portal — use Billing → Payment method & invoices");
      setBusy(false);
    }
  };

  const company = info?.company_name || user?.tenant_name || "This workspace";
  const isAdmin = info ? info.is_admin : ["super_admin", "admin"].includes(user?.role);
  const trialEnded = (info?.reason || user?.dunning?.reason) === "trial_expired";

  if (trialEnded) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="workspace-locked-page">
        <div className="w-full max-w-2xl">
          <div className="rounded-2xl border border-amber-200 dark:border-amber-900/60 bg-card shadow-lg overflow-hidden" data-testid="workspace-locked-trial">
            <div className="bg-amber-600 text-white px-6 py-6 flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0"><Timer size={28} weight="fill" /></div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-amber-100 font-semibold">Free trial ended</p>
                <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] leading-tight" data-testid="workspace-locked-title">{company}'s free trial has ended</h1>
                <p className="text-sm text-amber-100 mt-1">Everything you set up is safe and still here to view. Saving changes is paused until the workspace is subscribed.</p>
              </div>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid sm:grid-cols-2 gap-3" data-testid="workspace-locked-facts">
                <div className="rounded-lg border border-border p-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Trial ended</p>
                  <p className="text-sm font-semibold" data-testid="workspace-locked-trial-ended">{day(info?.trial_ends_at || user?.dunning?.trial_ends_at) || "—"}</p>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Plan</p>
                  <p className="text-sm font-semibold capitalize" data-testid="workspace-locked-plan">{(info?.plan || "").replace(/_/g, " ") || "—"}</p>
                </div>
              </div>
              {isAdmin ? (
                <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/60 p-4" data-testid="workspace-locked-admin-actions">
                  <p className="text-sm font-semibold">Subscribe to pick up right where you left off</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Choose your seats and add-ons on the subscribe page — full access is restored the moment the subscription starts.</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button asChild className="bg-amber-600 hover:bg-amber-700 text-white">
                      <Link to="/platform/billing" data-testid="workspace-locked-subscribe"><CreditCard size={16} className="mr-1.5" /> Subscribe now</Link>
                    </Button>
                    <Button asChild variant="outline"><Link to="/pricing" data-testid="workspace-locked-view-plans">Compare plans</Link></Button>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/60 p-4" data-testid="workspace-locked-staff-actions">
                  <p className="text-sm font-semibold">Ask an admin to subscribe</p>
                  {info?.owner?.email ? (
                    <p className="text-xs text-muted-foreground mt-0.5">The workspace owner is <span className="font-medium text-foreground" data-testid="workspace-locked-owner">{info.owner.name || info.owner.email}</span> — they can subscribe from Billing in a couple of minutes.</p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-0.5">Any admin of {company} can subscribe from Billing to restore full access.</p>
                  )}
                  {info?.owner?.email && (
                    <Button asChild size="sm" className="mt-3" variant="outline">
                      <a href={`mailto:${info.owner.email}?subject=${encodeURIComponent(`${company} free trial has ended`)}&body=${encodeURIComponent(`Hi ${info.owner.name || ""},\n\nFixlr says ${company}'s free trial has ended and the workspace is read-only. Could you subscribe from Billing when you get a moment so we can carry on?\n\nThanks!`)}`} data-testid="workspace-locked-email-owner">
                        <EnvelopeSimple size={14} className="mr-1.5" /> Email {info.owner.name?.split(" ")[0] || "the owner"}
                      </a>
                    </Button>
                  )}
                </div>
              )}
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-2">WHAT STILL WORKS</p>
                <ul className="grid sm:grid-cols-2 gap-2" data-testid="workspace-locked-still-works">
                  {STILL_WORKS.map(({ icon: Icon, label }) => (
                    <li key={label} className="flex items-center gap-2 text-sm text-muted-foreground"><Icon size={15} className="text-emerald-600 shrink-0" /> {label}</li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
                <Button variant="ghost" size="sm" onClick={() => navigate(from)} data-testid="workspace-locked-back">
                  <ArrowLeft size={14} className="mr-1" /> Back to what I was doing
                </Button>
                <p className="text-[10px] text-muted-foreground">This page refreshes itself and sends you back as soon as the subscription starts.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="workspace-locked-page">
      <div className="w-full max-w-2xl">
        <div className="rounded-2xl border border-red-200 dark:border-red-900/60 bg-card shadow-lg overflow-hidden">
          <div className="bg-red-700 text-white px-6 py-6 flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0"><LockKey size={28} weight="fill" /></div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-red-100 font-semibold">Read-only mode</p>
              <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] leading-tight" data-testid="workspace-locked-title">{company} is read-only right now</h1>
              <p className="text-sm text-red-100 mt-1">Nothing has been lost — everything you can see is safe. Saving changes is paused until the subscription payment goes through.</p>
            </div>
          </div>

          <div className="p-6 space-y-5">
            <div className="grid sm:grid-cols-3 gap-3" data-testid="workspace-locked-facts">
              <div className="rounded-lg border border-border p-3">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Outstanding</p>
                <p className="text-lg font-bold font-[Chivo] text-red-600" data-testid="workspace-locked-amount">{info?.amount != null ? money(info.amount, info.currency) : "—"}</p>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Card declined</p>
                <p className="text-sm font-semibold" data-testid="workspace-locked-failed">{day(info?.failed_at) || "—"}</p>
                {info?.attempt_count > 1 && <p className="text-[10px] text-muted-foreground">{info.attempt_count} attempts so far</p>}
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Next Stripe retry</p>
                <p className="text-sm font-semibold" data-testid="workspace-locked-retry">{day(info?.next_attempt_at) || "No retry scheduled"}</p>
              </div>
            </div>

            {isAdmin ? (
              <div className="rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/60 p-4" data-testid="workspace-locked-admin-actions">
                <p className="text-sm font-semibold">You can fix this in under a minute</p>
                <p className="text-xs text-muted-foreground mt-0.5">Update the card in the secure Stripe portal — Stripe retries straight away and access is restored the moment it clears.</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button onClick={updateCard} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="workspace-locked-update-card">
                    <CreditCard size={16} className="mr-1.5" /> {busy ? "Opening Stripe…" : "Update card"}
                  </Button>
                  <Button asChild variant="outline"><Link to="/platform/billing" data-testid="workspace-locked-view-billing">View billing</Link></Button>
                  {info?.hosted_invoice_url && (
                    <Button asChild variant="ghost" className="text-xs">
                      <a href={info.hosted_invoice_url} target="_blank" rel="noopener noreferrer" data-testid="workspace-locked-pay-invoice">Pay the Stripe invoice directly <ArrowSquareOut size={12} className="ml-1" /></a>
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/60 p-4" data-testid="workspace-locked-staff-actions">
                <p className="text-sm font-semibold">Ask an admin to update the card</p>
                {info?.owner?.email ? (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    The workspace owner is <span className="font-medium text-foreground" data-testid="workspace-locked-owner">{info.owner.name || info.owner.email}</span>. A quick note is usually all it takes.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-0.5">Any admin of {company} can restore full access from Billing.</p>
                )}
                {info?.owner?.email && (
                  <Button asChild size="sm" className="mt-3" variant="outline">
                    <a href={`mailto:${info.owner.email}?subject=${encodeURIComponent(`${company} is read-only — card update needed`)}&body=${encodeURIComponent(`Hi ${info.owner.name || ""},\n\nFixlr says ${company} is read-only because the subscription payment${info.amount != null ? ` of ${money(info.amount, info.currency)}` : ""} didn't go through. Could you update the card in Billing when you get a moment?\n\nThanks!`)}`} data-testid="workspace-locked-email-owner">
                      <EnvelopeSimple size={14} className="mr-1.5" /> Email {info.owner.name?.split(" ")[0] || "the owner"}
                    </a>
                  </Button>
                )}
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">WHAT STILL WORKS</p>
              <ul className="grid sm:grid-cols-2 gap-2" data-testid="workspace-locked-still-works">
                {STILL_WORKS.map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-center gap-2 text-sm text-muted-foreground"><Icon size={15} className="text-emerald-600 shrink-0" /> {label}</li>
                ))}
              </ul>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => navigate(from)} data-testid="workspace-locked-back">
                <ArrowLeft size={14} className="mr-1" /> Back to what I was doing
              </Button>
              <p className="text-[10px] text-muted-foreground">This page refreshes itself and sends you back as soon as the payment clears.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
